import Page from '@pelckmans/elementis/components/page';
import Tabs from '@pelckmans/elementis/components/tabs';
import { useTranslation } from 'react-i18next';
import MyGroupsContextProvider from './context/MyGroupsContext';
import useGroups, { SchoolYear } from './hooks/useGroups';
import useDocumentTitle from '../../hooks/useDocumentTitle';
import ApplicationLevelLayout from '../../layout/ApplicationLevelLayout';
import OverviewMyGroups from './components/OverviewMyGroups';
import ContentHeader from '../../layout/content/ContentHeader';
import ContentBody from '../../layout/content/ContentBody';
import {
  withScrollThreshold,
  WithScrollThresholdProps,
} from '../../shared-components/elementis/hoc/withScrollThreshold';
import { useRoutes } from '../../hooks/useRoutes';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useEffect } from 'react';

export const SCHOOL_YEAR_TABS = {
  PREVIOUS: 'PREVIOUS_SCHOOLYEAR',
  CURRENT: 'CURRENT_SCHOOLYEAR',
  NEXT: 'NEXT_SCHOOLYEAR',
} as const;

type TabId = typeof SCHOOL_YEAR_TABS[keyof typeof SCHOOL_YEAR_TABS];
type MyGroupRouteKey = `MY_GROUPS.${TabId}`;

const getSchoolYearDisplayName = (sy: SchoolYear) => {
  if (sy.hasPassed) return 'PAGES.MY_GROUPS.TAB_LIST.TABS.PREVIOUS';
  if (sy.isCurrent) return 'PAGES.MY_GROUPS.TAB_LIST.TABS.CURRENT';
  return 'PAGES.MY_GROUPS.TAB_LIST.TABS.NEXT';
};

const matchers = {
  [SCHOOL_YEAR_TABS.PREVIOUS]: (sy: SchoolYear) => sy.hasPassed,
  [SCHOOL_YEAR_TABS.CURRENT]: (sy: SchoolYear) => sy.isCurrent,
  [SCHOOL_YEAR_TABS.NEXT]: (sy: SchoolYear) => !sy.hasPassed && !sy.isCurrent,
} as const;

const findSchoolYear = (schoolYears: SchoolYear[], tab: TabId) => schoolYears.find(matchers[tab]) ?? null;

export function MyGroups({ pastThreshold }: WithScrollThresholdProps) {
  const { t } = useTranslation();
  const { schoolYears } = useGroups();
  const { getRoute } = useRoutes();
  const navigate = useNavigate();
  const location = useLocation();

  useDocumentTitle(t('PAGES.MY_GROUPS.PAGE_TITLE'));

  const selectedTab: TabId | undefined = (Object.values(SCHOOL_YEAR_TABS) as TabId[]).find(tab =>
    location.pathname.includes(getRoute(`MY_GROUPS.${tab}` as MyGroupRouteKey)),
  );

  const onTabChange = (tab: string) => {
    if (tab === selectedTab) return;

    navigate(getRoute(`MY_GROUPS.${tab}` as MyGroupRouteKey));
  };

  return (
    <ApplicationLevelLayout>
      <Page>
        <Tabs selectedTab={selectedTab} onTabChange={onTabChange}>
          <ContentHeader
            cover={
              <Page.Header
                density={pastThreshold ? 'compact' : 'normal'}
                headerText={t('PAGES.MY_GROUPS.TITLE')}
                tabs={
                  schoolYears.length ? (
                    <Tabs.TabList aria-label={t('PAGES.MY_GROUPS.TAB_LIST.A11Y_LABEL')}>
                      {(Object.values(SCHOOL_YEAR_TABS) as TabId[]).map(tabId => {
                        const sy = findSchoolYear(schoolYears, tabId);

                        return (
                          <Tabs.Tab key={tabId} id={tabId}>
                            {t(getSchoolYearDisplayName(sy!))}
                          </Tabs.Tab>
                        );
                      })}
                    </Tabs.TabList>
                  ) : undefined
                }
              />
            }
          />
          <ContentBody>
            <Tabs.TabsContent>
              {(Object.values(SCHOOL_YEAR_TABS) as TabId[]).map(tabId => {
                return (
                  <Tabs.TabPanel key={tabId} id={tabId}>
                    <Outlet />
                  </Tabs.TabPanel>
                );
              })}
            </Tabs.TabsContent>
          </ContentBody>
        </Tabs>
      </Page>
    </ApplicationLevelLayout>
  );
}

export function GroupsTabContent({ tab }: { tab: TabId }) {
  const { schoolYears, onSchoolYearChange } = useGroups();

  const schoolYear = findSchoolYear(schoolYears, tab);

  useEffect(() => {
    if (schoolYear) onSchoolYearChange(schoolYear);
  }, [schoolYear, onSchoolYearChange]);

  if (!schoolYear) return null;

  return <OverviewMyGroups schoolYear={schoolYear} />;
}

export default withScrollThreshold(function MyGroupsWithContext(props) {
  return (
    <MyGroupsContextProvider>
      <MyGroups {...props} />
    </MyGroupsContextProvider>
  );
});
